// @ts-nocheck
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import type {
  PatchedReaderProfile,
  ReaderProfile,
  ReaderProfileList,
} from "../../models";
import { customInstance } from "../../custom-instance";

// https://stackoverflow.com/questions/49579094/typescript-conditional-types-filter-out-readonly-properties-pick-only-requir/49579497#49579497
type IfEquals<X, Y, A = X, B = never> = (<T>() => T extends X ? 1 : 2) extends <
  T
>() => T extends Y ? 1 : 2
  ? A
  : B;

type WritableKeys<T> = {
  [P in keyof T]-?: IfEquals<
    { [Q in P]: T[P] },
    { -readonly [Q in P]: T[P] },
    P
  >;
}[keyof T];

type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (
  k: infer I
) => void
  ? I
  : never;
type DistributeReadOnlyOverUnions<T> = T extends any ? NonReadonly<T> : never;

type Writable<T> = Pick<T, WritableKeys<T>>;
type NonReadonly<T> = [T] extends [UnionToIntersection<T>]
  ? {
      [P in keyof Writable<T>]: T[P] extends object
        ? NonReadonly<NonNullable<T[P]>>
        : T[P];
    }
  : DistributeReadOnlyOverUnions<T>;

type SecondParameter<T extends (...args: any) => any> = Parameters<T>[1];

/**
 * List all reader profiles for the authenticated user
 */
export const accountReaderProfilesList = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<ReaderProfileList[]>(
    { url: `/api/account/reader-profiles/`, method: "GET", signal },
    options
  );
};

export const getAccountReaderProfilesListQueryKey = () => {
  return [`/api/account/reader-profiles/`] as const;
};

export const getAccountReaderProfilesListQueryOptions = <
  TData = Awaited<ReturnType<typeof accountReaderProfilesList>>,
  TError = unknown
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof accountReaderProfilesList>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getAccountReaderProfilesListQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof accountReaderProfilesList>>
  > = ({ signal }) => accountReaderProfilesList(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof accountReaderProfilesList>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type AccountReaderProfilesListQueryResult = NonNullable<
  Awaited<ReturnType<typeof accountReaderProfilesList>>
>;
export type AccountReaderProfilesListQueryError = unknown;

export function useAccountReaderProfilesList<
  TData = Awaited<ReturnType<typeof accountReaderProfilesList>>,
  TError = unknown
>(options: {
  query: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof accountReaderProfilesList>>,
      TError,
      TData
    >
  > &
    Pick<
      DefinedInitialDataOptions<
        Awaited<ReturnType<typeof accountReaderProfilesList>>,
        TError,
        Awaited<ReturnType<typeof accountReaderProfilesList>>
      >,
      "initialData"
    >;
  request?: SecondParameter<typeof customInstance>;
}): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useAccountReaderProfilesList<
  TData = Awaited<ReturnType<typeof accountReaderProfilesList>>,
  TError = unknown
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof accountReaderProfilesList>>,
      TError,
      TData
    >
  > &
    Pick<
      UndefinedInitialDataOptions<
        Awaited<ReturnType<typeof accountReaderProfilesList>>,
        TError,
        Awaited<ReturnType<typeof accountReaderProfilesList>>
      >,
      "initialData"
    >;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useAccountReaderProfilesList<
  TData = Awaited<ReturnType<typeof accountReaderProfilesList>>,
  TError = unknown
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof accountReaderProfilesList>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useAccountReaderProfilesList<
  TData = Awaited<ReturnType<typeof accountReaderProfilesList>>,
  TError = unknown
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof accountReaderProfilesList>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getAccountReaderProfilesListQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Create a new reader profile for the authenticated user
 */
export const accountReaderProfilesCreate = (
  readerProfile: NonReadonly<ReaderProfile>,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<ReaderProfile>(
    {
      url: `/api/account/reader-profiles/`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: readerProfile,
      signal,
    },
    options
  );
};

export const getAccountReaderProfilesCreateMutationOptions = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountReaderProfilesCreate>>,
    TError,
    { data: NonReadonly<ReaderProfile> },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof accountReaderProfilesCreate>>,
  TError,
  { data: NonReadonly<ReaderProfile> },
  TContext
> => {
  const mutationKey = ["accountReaderProfilesCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof accountReaderProfilesCreate>>,
    { data: NonReadonly<ReaderProfile> }
  > = (props) => {
    const { data } = props ?? {};

    return accountReaderProfilesCreate(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AccountReaderProfilesCreateMutationResult = NonNullable<
  Awaited<ReturnType<typeof accountReaderProfilesCreate>>
>;
export type AccountReaderProfilesCreateMutationBody =
  NonReadonly<ReaderProfile>;
export type AccountReaderProfilesCreateMutationError = unknown;

export const useAccountReaderProfilesCreate = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountReaderProfilesCreate>>,
    TError,
    { data: NonReadonly<ReaderProfile> },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof accountReaderProfilesCreate>>,
  TError,
  { data: NonReadonly<ReaderProfile> },
  TContext
> => {
  const mutationOptions =
    getAccountReaderProfilesCreateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Retrieve a specific reader profile
 */
export const accountReaderProfilesRetrieve = (
  id: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<ReaderProfile>(
    { url: `/api/account/reader-profiles/${id}/`, method: "GET", signal },
    options
  );
};

export const getAccountReaderProfilesRetrieveQueryKey = (id: number) => {
  return [`/api/account/reader-profiles/${id}/`] as const;
};

export const getAccountReaderProfilesRetrieveQueryOptions = <
  TData = Awaited<ReturnType<typeof accountReaderProfilesRetrieve>>,
  TError = unknown
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof accountReaderProfilesRetrieve>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getAccountReaderProfilesRetrieveQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof accountReaderProfilesRetrieve>>
  > = ({ signal }) => accountReaderProfilesRetrieve(id, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof accountReaderProfilesRetrieve>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type AccountReaderProfilesRetrieveQueryResult = NonNullable<
  Awaited<ReturnType<typeof accountReaderProfilesRetrieve>>
>;
export type AccountReaderProfilesRetrieveQueryError = unknown;

export function useAccountReaderProfilesRetrieve<
  TData = Awaited<ReturnType<typeof accountReaderProfilesRetrieve>>,
  TError = unknown
>(
  id: number,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof accountReaderProfilesRetrieve>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountReaderProfilesRetrieve>>,
          TError,
          Awaited<ReturnType<typeof accountReaderProfilesRetrieve>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useAccountReaderProfilesRetrieve<
  TData = Awaited<ReturnType<typeof accountReaderProfilesRetrieve>>,
  TError = unknown
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof accountReaderProfilesRetrieve>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof accountReaderProfilesRetrieve>>,
          TError,
          Awaited<ReturnType<typeof accountReaderProfilesRetrieve>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useAccountReaderProfilesRetrieve<
  TData = Awaited<ReturnType<typeof accountReaderProfilesRetrieve>>,
  TError = unknown
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof accountReaderProfilesRetrieve>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useAccountReaderProfilesRetrieve<
  TData = Awaited<ReturnType<typeof accountReaderProfilesRetrieve>>,
  TError = unknown
>(
  id: number,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof accountReaderProfilesRetrieve>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getAccountReaderProfilesRetrieveQueryOptions(
    id,
    options
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Full update of a reader profile
 */
export const accountReaderProfilesUpdate = (
  id: number,
  readerProfile: NonReadonly<ReaderProfile>,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<ReaderProfile>(
    {
      url: `/api/account/reader-profiles/${id}/`,
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      data: readerProfile,
    },
    options
  );
};

export const getAccountReaderProfilesUpdateMutationOptions = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountReaderProfilesUpdate>>,
    TError,
    { id: number; data: NonReadonly<ReaderProfile> },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof accountReaderProfilesUpdate>>,
  TError,
  { id: number; data: NonReadonly<ReaderProfile> },
  TContext
> => {
  const mutationKey = ["accountReaderProfilesUpdate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof accountReaderProfilesUpdate>>,
    { id: number; data: NonReadonly<ReaderProfile> }
  > = (props) => {
    const { id, data } = props ?? {};

    return accountReaderProfilesUpdate(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AccountReaderProfilesUpdateMutationResult = NonNullable<
  Awaited<ReturnType<typeof accountReaderProfilesUpdate>>
>;
export type AccountReaderProfilesUpdateMutationBody =
  NonReadonly<ReaderProfile>;
export type AccountReaderProfilesUpdateMutationError = unknown;

export const useAccountReaderProfilesUpdate = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountReaderProfilesUpdate>>,
    TError,
    { id: number; data: NonReadonly<ReaderProfile> },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof accountReaderProfilesUpdate>>,
  TError,
  { id: number; data: NonReadonly<ReaderProfile> },
  TContext
> => {
  const mutationOptions =
    getAccountReaderProfilesUpdateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Partial update of a reader profile
 */
export const accountReaderProfilesPartialUpdate = (
  id: number,
  patchedReaderProfile: NonReadonly<PatchedReaderProfile>,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<ReaderProfile>(
    {
      url: `/api/account/reader-profiles/${id}/`,
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      data: patchedReaderProfile,
    },
    options
  );
};

export const getAccountReaderProfilesPartialUpdateMutationOptions = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountReaderProfilesPartialUpdate>>,
    TError,
    { id: number; data: NonReadonly<PatchedReaderProfile> },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof accountReaderProfilesPartialUpdate>>,
  TError,
  { id: number; data: NonReadonly<PatchedReaderProfile> },
  TContext
> => {
  const mutationKey = ["accountReaderProfilesPartialUpdate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof accountReaderProfilesPartialUpdate>>,
    { id: number; data: NonReadonly<PatchedReaderProfile> }
  > = (props) => {
    const { id, data } = props ?? {};

    return accountReaderProfilesPartialUpdate(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AccountReaderProfilesPartialUpdateMutationResult = NonNullable<
  Awaited<ReturnType<typeof accountReaderProfilesPartialUpdate>>
>;
export type AccountReaderProfilesPartialUpdateMutationBody =
  NonReadonly<PatchedReaderProfile>;
export type AccountReaderProfilesPartialUpdateMutationError = unknown;

export const useAccountReaderProfilesPartialUpdate = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountReaderProfilesPartialUpdate>>,
    TError,
    { id: number; data: NonReadonly<PatchedReaderProfile> },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof accountReaderProfilesPartialUpdate>>,
  TError,
  { id: number; data: NonReadonly<PatchedReaderProfile> },
  TContext
> => {
  const mutationOptions =
    getAccountReaderProfilesPartialUpdateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Delete a reader profile
 */
export const accountReaderProfilesDestroy = (
  id: number,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<void>(
    { url: `/api/account/reader-profiles/${id}/`, method: "DELETE" },
    options
  );
};

export const getAccountReaderProfilesDestroyMutationOptions = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountReaderProfilesDestroy>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof accountReaderProfilesDestroy>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationKey = ["accountReaderProfilesDestroy"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof accountReaderProfilesDestroy>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return accountReaderProfilesDestroy(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AccountReaderProfilesDestroyMutationResult = NonNullable<
  Awaited<ReturnType<typeof accountReaderProfilesDestroy>>
>;

export type AccountReaderProfilesDestroyMutationError = unknown;

export const useAccountReaderProfilesDestroy = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof accountReaderProfilesDestroy>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof accountReaderProfilesDestroy>>,
  TError,
  { id: number },
  TContext
> => {
  const mutationOptions =
    getAccountReaderProfilesDestroyMutationOptions(options);

  return useMutation(mutationOptions);
};
